import React, { useEffect, useState } from "react";
import {
    empAgreementDetailsApi,
    empAgreementDetailsEsignApi,
    empAgreementDetailsOtpApi,
} from "../apis/customer/customer-apis";
import errorToast from "../utils/toast/errorToast";
import { useParams } from "react-router-dom";
import EmpInputFieldEsign from "../components/inputSignEmpField";

const AgreementPage = () => {
    const params = useParams();

    const [viewData, setViewData] = useState("");
    const [requiredFields, setRequiredFields] = useState([]);
    const [formValues, setFormValues] = useState({});
    const [otp, setOtp] = useState("");

    // Agreement completed state
    const [isCompleted, setIsCompleted] = useState(false);

    // OTP popup state
    const [showOtpPopup, setShowOtpPopup] = useState(false);

    // OTP resend timer
    const [resendTimer, setResendTimer] = useState(0);

    // Loading states
    const [otpLoading, setOtpLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);

    const predefinedOptions = [
        { label: "Name", value: "<strong>[NAME]</strong>" },
        { label: "Address", value: "<strong>[Address]</strong>" },
        { label: "Pan", value: "<strong>[Pan]</strong>" },
        { label: "Email", value: "<strong>[Email]</strong>" },
        { label: "Date Of Birth", value: "<strong>[DateOfBirth]</strong>" },
        { label: "Name Of Father", value: "<strong>[NameOfFather]</strong>" },
        { label: "Name Of Mother", value: "<strong>[NameOfMother]</strong>" },
        { label: "Phone", value: "<strong>[Phone]</strong>" },
        { label: "E-Sign", value: "<strong>[Esign]</strong>" },
    ];

    /*
     * ============================================================
     * CHECK AGREEMENT
     * ============================================================
     */

    const viewFnc = async () => {
        try {
            const res = await empAgreementDetailsApi({
                id: params?.id,
            });

            if (res.status === 200) {
                const data = res.data?.data;

                /*
                 * If agreement is already submitted or approved,
                 * don't show the agreement form.
                 */
                if (
                    data?.status === "submitted" ||
                    data?.status === "approved"
                ) {
                    setIsCompleted(true);
                    return;
                }

                setViewData(data);
            }
        } catch (error) {
            console.log("view agreement error", error);
            errorToast("Please try again later");
        }
    };

    useEffect(() => {
        viewFnc();
    }, []);

    /*
     * ============================================================
     * OTP RESEND TIMER
     * ============================================================
     */

    useEffect(() => {
        if (resendTimer <= 0) {
            return;
        }

        const timer = setInterval(() => {
            setResendTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }

                return prev - 1;
            });
        }, 1000);

        return () => {
            clearInterval(timer);
        };
    }, [resendTimer]);

    /*
     * ============================================================
     * FIND REQUIRED FIELDS
     * ============================================================
     */

    useEffect(() => {
        if (viewData?.template?.agreement_details) {
            const html = viewData.template.agreement_details;

            const fields = predefinedOptions.filter((opt) =>
                html.includes(opt.value),
            );

            setRequiredFields(fields);

            const initialValues = {};

            fields.forEach((f) => {
                const key = f.value.replace(/<\/?strong>|\[|\]/g, "");
                initialValues[key] = "";
            });

            setFormValues(initialValues);
        }
    }, [viewData]);

    /*
     * ============================================================
     * FORM CHANGE
     * ============================================================
     */

    const handleChange = (key, value) => {
        setFormValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleUpdate = (key, value) => {
        setFormValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    /*
     * ============================================================
     * SEND OTP
     * ============================================================
     */

    const sendOtp = async () => {
        if (otpLoading) {
            return;
        }

        // ============================================================
        // VALIDATE ALL REQUIRED FIELDS
        // ============================================================

        for (const field of requiredFields) {
            const key = field.value.replace(/<\/?strong>|\[|\]/g, "");

            const value = formValues[key];

            if (
                value === undefined ||
                value === null ||
                String(value).trim() === ""
            ) {
                errorToast(`Please enter ${field.label}`);
                return;
            }
        }

        // ============================================================
        // ALL FIELDS ARE VALID -> SEND OTP
        // ============================================================

        try {
            setOtpLoading(true);

            const res = await empAgreementDetailsOtpApi({
                id: params?.id,
            });

            if (res.status === 200) {
                // Clear old OTP
                setOtp("");

                // Open OTP popup
                setShowOtpPopup(true);

                // Start 60 second resend timer
                setResendTimer(60);
            } else {
                errorToast(
                    res?.data?.message ||
                        "Something went wrong. Please try again.",
                );
            }
        } catch (error) {
            console.log("send otp error", error);

            errorToast(
                error?.response?.data?.message || "Please try again later",
            );
        } finally {
            setOtpLoading(false);
        }
    };

    /*
     * ============================================================
     * HANDLE SUBMIT / VERIFY OTP
     * ============================================================
     */

    const handleSubmit = async () => {
        if (otp.length !== 6) {
            errorToast("Please enter the 6 digit OTP");
            return;
        }

        if (submitLoading) {
            return;
        }

        let html = viewData?.template?.agreement_details;

        let esign = null;

        /*
         * Replace all agreement placeholders with
         * the values entered by the user.
         */
        predefinedOptions.forEach((opt) => {
            const key = opt.value.replace(/<\/?strong>|\[|\]/g, "");

            if (formValues[key]) {
                if (key === "Esign") {
                    esign = formValues[key];

                    html = html
                        .split(opt.value)
                        .join(
                            `<img src="${formValues[key]}" alt="signature" style="height:80px;margin-top:10px;" />`,
                        );
                } else {
                    html = html
                        .split(opt.value)
                        .join(`<strong>${formValues[key]}</strong> `);
                }
            }
        });

        /*
         * E-signature is required if the agreement
         * contains an E-Sign field.
         */
        if (hasEsign && !esign) {
            errorToast("Please provide your e-signature");
            return;
        }

        try {
            setSubmitLoading(true);

            const res = await empAgreementDetailsEsignApi({
                id: params?.id,
                esign: esign,
                otp: otp,
                html: html,
            });

            if (res.status === 200) {
                /*
                 * Agreement successfully submitted.
                 */
                setShowOtpPopup(false);
                setOtp("");
                setResendTimer(0);

                setIsCompleted(true);
            } else {
                /*
                 * Verification failed.
                 *
                 * Close OTP popup as requested.
                 */
                setShowOtpPopup(false);
                setOtp("");
                setResendTimer(0);

                errorToast(
                    res?.data?.message ||
                        "Something went wrong. Please try again.",
                );
            }
        } catch (error) {
            console.log("submit agreement error", error);

            /*
             * Close OTP popup on error.
             */
            setShowOtpPopup(false);
            setOtp("");
            setResendTimer(0);

            errorToast(
                error?.response?.data?.message ||
                    "Something went wrong. Please try again.",
            );
        } finally {
            setSubmitLoading(false);
        }
    };

    /*
     * ============================================================
     * REQUIRED E-SIGN
     * ============================================================
     */

    const hasEsign = requiredFields.some((f) => f.label === "E-Sign");

    const esignInputObj = {
        key: "Esign",
        PlaceholderText: "E-Signature",
        value: formValues["Esign"],
    };

    /*
     * ============================================================
     * AGREEMENT COMPLETED SCREEN
     * ============================================================
     */

    if (isCompleted) {
        return (
            <div className="min-h-screen bg-gradient-to-r from-[#012a2c] via-[#013c3f] to-[#012a2c] flex items-center justify-center p-6">
                <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center">
                    <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                        <svg
                            className="w-8 h-8 text-emerald-600"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                    </div>

                    <h1 className="text-2xl font-bold text-slate-900">
                        Agreement Completed
                    </h1>

                    <p className="mt-3 text-sm text-slate-500 leading-relaxed">
                        Your agreement has been successfully submitted and
                        completed.
                    </p>

                    {viewData?.code && (
                        <div className="mt-6 bg-slate-50 border border-slate-200 rounded-lg p-4">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                Agreement Reference
                            </div>

                            <div className="mt-1 text-sm font-semibold text-slate-800">
                                {viewData.code}
                            </div>
                        </div>
                    )}

                    <div className="mt-6 text-xs text-slate-400">
                        The agreement has already been completed and cannot be
                        submitted again.
                    </div>
                </div>
            </div>
        );
    }

    /*
     * ============================================================
     * MAIN PAGE
     * ============================================================
     */

    return (
        <div className="min-h-screen bg-gradient-to-r from-[#012a2c] via-[#013c3f] to-[#012a2c] p-10">
            <div className="w-full max-w-[30rem] mx-auto flex flex-col items-center">
                <h1 className="text-2xl text-white font-semibold mb-8">
                    Agreement Form
                </h1>

                {requiredFields
                    .filter((f) => f.label !== "E-Sign")
                    .map((field, index) => {
                        const key = field.value.replace(
                            /<\/?strong>|\[|\]/g,
                            "",
                        );

                        return (
                            <div
                                key={index}
                                className="mb-4 w-full max-w-[500px]"
                            >
                                <label className="block mb-1 text-gray-200">
                                    {field.label}
                                </label>

                                <input
                                    type={
                                        key === "DateOfBirth" ? "date" : "text"
                                    }
                                    className="w-full bg-yellow-100 border border-[#1e4b52] text-dark p-2 px-3 rounded"
                                    value={formValues[key] || ""}
                                    onChange={(e) =>
                                        handleChange(key, e.target.value)
                                    }
                                    placeholder={`Enter ${field.label}`}
                                />
                            </div>
                        );
                    })}

                {hasEsign && (
                    <EmpInputFieldEsign
                        inputObj={esignInputObj}
                        onUpdate={handleUpdate}
                    />
                )}

                {requiredFields.length > 0 && (
                    <button
                        onClick={sendOtp}
                        disabled={otpLoading}
                        className="mt-8 bg-[#caa96a] text-black px-5 py-2 rounded font-medium w-full max-w-[500px] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {otpLoading ? "Sending OTP..." : "Submit"}
                    </button>
                )}
            </div>
            <div className="mt-10 max-w-[1100px] mx-auto bg-white border border-[#1e4b52] rounded-xl p-8 shadow-lg">
                <h2 className="text-3xl text-[#021f22] font-semibold mb-6 border-l-4 border-[#caa96a] pl-4">
                    {viewData?.template?.title ?? "--"}
                </h2>

                <div
                    className="leading-relaxed"
                    dangerouslySetInnerHTML={{
                        __html: viewData?.template?.agreement_details || "",
                    }}
                />
            </div>
            {showOtpPopup && (
                <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4">
                    <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
                        <div className="text-center">
                            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                                <svg
                                    className="w-7 h-7 text-emerald-600"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 11c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z"
                                    />

                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M6 20a6 6 0 0112 0"
                                    />
                                </svg>
                            </div>

                            <h2 className="mt-4 text-xl font-bold text-slate-900">
                                Verify Agreement
                            </h2>

                            <p className="mt-2 text-sm text-slate-500">
                                Enter the 6 digit OTP sent to your registered
                                mobile number.
                            </p>
                        </div>

                        <div className="mt-6">
                            <input
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={6}
                                autoFocus
                                value={otp}
                                onChange={(e) => {
                                    const value = e.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 6);

                                    setOtp(value);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && otp.length === 6) {
                                        handleSubmit();
                                    }
                                }}
                                className="w-full text-center tracking-[0.7em] text-2xl font-bold border border-slate-300 rounded-xl px-4 py-4 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                placeholder="••••••"
                            />
                        </div>

                        <div className="mt-5 text-center">
                            {resendTimer > 0 ? (
                                <div className="text-xs text-slate-400">
                                    Resend OTP in{" "}
                                    <span className="font-semibold text-slate-700">
                                        {resendTimer}s
                                    </span>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={sendOtp}
                                    disabled={otpLoading}
                                    className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
                                >
                                    {otpLoading ? "Sending..." : "Resend OTP"}
                                </button>
                            )}
                        </div>

                        <div className="mt-6 grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowOtpPopup(false);
                                    setOtp("");
                                    setResendTimer(0);
                                }}
                                disabled={submitLoading}
                                className="px-4 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={otp.length !== 6 || submitLoading}
                                className="px-4 py-3 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {submitLoading
                                    ? "Verifying..."
                                    : "Verify & Complete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgreementPage;
